import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  ConfirmResponse,
  useConfirmDialog,
} from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Loader from "_shared/_views/loading/loader";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import MarketplaceTeam from "marketplace/teams/team";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { NavigateFunction } from "react-router";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import TeamLeaderSelector from "work/work-agreements/teams/vendor-team/_views/leader/team-leader-selector";
import VendorTeamSelection from "work/work-agreements/teams/vendor-team/_views/marketplace/marketplace-vendor-team-selector";
import VendorTeamMemberSelection from "work/work-agreements/teams/vendor-team/_views/members/vendor-team-member-selection";

const MainContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));
const FromMarketplaceButton = styled(Button)(() => ({
  width: "fit-content",
}));
const TeamContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));
const TeamList = styled(List)(({ theme }) => ({
  margin: 0,
  marginTop: 0,
  minWidth: theme.spacing(40),
  paddingBottom: theme.spacing(3),
  "&:first-of-type": {
    marginRight: theme.spacing(3),
  },
  [theme.breakpoints.down("lg")]: {
    "&:first-of-type": {
      marginRight: 0,
    },
  },
}));
const TeamListSubheader = styled(ListSubheader)(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.common.white,
  color: theme.palette.common.black,
  display: "flex",
  fontSize: "1.3em",
  justifyContent: "space-between",
  padding: 0,
  "& > button": {
    height: "fit-content",
    width: "fit-content",
  },
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  paddingTop: theme.spacing(1),
}));
const TeamMemberLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.black,
  cursor: "pointer",
  textDecorationColor: theme.palette.common.black,
}));
const RemoveIcon = styled(DeleteIcon)(({ theme }) => ({
  color: theme.palette.error.main,
}));
const TeamLeaderInput = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(1, 0),
  minWidth: theme.spacing(25),
}));
const TeamLoader = styled(Loader)(() => ({
  justifyContent: "flex-start",
}));

type EditVendorTeamProps = {
  className?: string;
  leader: EntityMember | null;
  members: EntityMember[];
  disableEditing?: boolean;
  navigate?: NavigateFunction;
  disableComments: boolean;
  hideCommentButton?: boolean;
  activeReviewField?: ProposalField;
  onLeaderUpdated?: (leader: EntityMember | null) => void;
  onMemberAdded?: (member: EntityMember) => void;
  onMemberRemoved?: (userId?: Guid) => void;
  onMarketplaceTeamSelected?: (team: MarketplaceTeam) => void;
  commentThreads?: CommentThread[];
  commentsToolTip: string;
  onCommentsClicked?: (proposalField: ProposalField, name?: string) => void;
};

export default function EditVendorTeam(props: Readonly<EditVendorTeamProps>) {
  const {
    className,
    leader,
    members,
    commentThreads,
    disableEditing,
    navigate,
    disableComments,
    hideCommentButton,
    commentsToolTip,
    activeReviewField,
    onCommentsClicked,
    onLeaderUpdated,
    onMemberAdded,
    onMemberRemoved,
    onMarketplaceTeamSelected,
  } = props;

  const memberUserIds: string[] = useMemo(() => {
    return members.map((member) => member.userId.value);
  }, [members]);

  const individualsToFetch = useMemo(() => {
    const ids = new Set<string>();
    if (leader) ids.add(leader.userId.value);
    memberUserIds.forEach((userId) => ids.add(userId));
    return ids;
  }, [leader, memberUserIds]);

  const individualQuery = useGetIndividualsByUserIdsQuery(
    individualsToFetch.size ? { userIds: individualsToFetch } : skipToken,
  );

  const individuals: Individual[] = useMemo(() => {
    return Individual.fromJSONArray(individualQuery.data) ?? [];
  }, [individualQuery.data]);

  const individualMap: Map<string, Individual> = useMemo(() => {
    const map = new Map<string, Individual>();
    individuals.forEach((individual) => {
      if (individual.userId?.value) {
        map.set(individual.userId.value, individual);
      }
    });
    return map;
  }, [individuals]);

  const leaderIndividual: Individual | undefined = useMemo(() => {
    if (!leader) return undefined;
    return individualMap.get(leader.userId.value);
  }, [leader, individualMap]);

  const memberIndividuals: Individual[] = useMemo(() => {
    if (memberUserIds.length === 0) return [];
    return memberUserIds
      .map((userId) => individualMap.get(userId))
      .filter(
        (individual): individual is Individual => individual !== undefined,
      );
  }, [memberUserIds, individualMap]);

  const userPermissions = useSelector(getSessionPermissions);

  const [teamLeaderSelectorAnchor, setTeamLeaderSelectorAnchor] =
    React.useState<HTMLDivElement>();

  const confirm = useConfirmDialog();
  const { openDialog, popDialog } = useDialog();
  const session = useSession();

  /**
   * Checks if the user with the given id is already selected or invited to the vendor-team
   *
   * @param userId Id of the user to check
   * @returns True if the user is already selected or invited to the vendor-team, false otherwise
   */
  const getIsMemberAlreadyOnTeam = useCallback(
    (userId?: Guid): boolean => {
      if (!userId) return false;

      const memberUserIds = memberIndividuals?.map((member) => member.userId);

      return (
        (memberUserIds?.some((member) => member?.isEqualTo(userId)) ?? false) ||
        (leaderIndividual?.userId?.isEqualTo(userId) ?? false)
      );
    },
    [leaderIndividual, memberIndividuals],
  );

  const getSelectedTeamMemberUserIds = useCallback((): Guid[] => {
    const userIds: Guid[] =
      memberIndividuals?.map((member) => member.userId) ?? [];
    if (leaderIndividual?.userId) {
      userIds.push(leaderIndividual.userId);
    }
    return userIds;
  }, [leaderIndividual, memberIndividuals]);

  /**
   * Handles when a new leader has been selected
   *
   * @param leader The new leader to replace the current leader with
   */
  const handleNewLeaderSelected = useCallback(
    async (leader: Individual) => {
      if (leaderIndividual) {
        const response = await confirm({
          title: "Replace Leader?",
          message: `Do you want to replace the current team leader with ${leader.fullName}?`,
          okButtonText: "Replace",
        });

        if (response === ConfirmResponse.Cancel) return;
      }

      if (
        memberIndividuals?.find((member) =>
          member.userId?.isEqualTo(leader.userId),
        )
      ) {
        onMemberRemoved?.(leader.userId);
        onLeaderUpdated?.(leader.toEntityMember());
        return;
      }

      onLeaderUpdated?.(leader?.toEntityMember());
    },
    [
      leaderIndividual,
      memberIndividuals,
      onLeaderUpdated,
      confirm,
      onMemberRemoved,
    ],
  );

  /**
   * Handles what happens when add leader is clicked
   */
  const beginLeaderSelection = useCallback(() => {
    openDialog({
      title: "Select Leader",
      component: (
        <VendorTeamMemberSelection
          onNewSelection={handleNewLeaderSelected}
          selectLeader={true}
          selectMember={false}
          selectedTeamMemberUserIds={getSelectedTeamMemberUserIds()}
          closeDialog={popDialog}
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }, [
    getSelectedTeamMemberUserIds,
    handleNewLeaderSelected,
    openDialog,
    popDialog,
  ]);

  const handleCoworkerSelectedAsLeader = useCallback(
    async (leader?: Individual) => {
      if (!leader) {
        onLeaderUpdated?.(null);
        return;
      }
      onLeaderUpdated?.(leader?.toEntityMember());
    },
    [onLeaderUpdated],
  );

  function handleSelectTeamLeaderClicked(
    event: React.MouseEvent<HTMLDivElement>,
  ) {
    const inputElements = event.currentTarget.getElementsByTagName("input");
    setTeamLeaderSelectorAnchor(
      inputElements.length > 0 ? inputElements[0] : event.currentTarget,
    );
  }

  /**
   * Handles when a new member has been selected
   *
   * @param teamMember The new member to add to the vendor-team
   */
  const handleNewMemberSelected = useCallback(
    (teamMember: Individual) => {
      if (getIsMemberAlreadyOnTeam(teamMember.userId)) {
        enqueueSnackbar("Selected user is already on vendor-team", {
          variant: "info",
          preventDuplicate: true,
          autoHideDuration: 7500,
        });
        return;
      }

      const newMemberList = memberIndividuals ? [...memberIndividuals] : [];
      newMemberList.push(teamMember);
      onMemberAdded?.(teamMember.toEntityMember());
    },
    [getIsMemberAlreadyOnTeam, onMemberAdded, memberIndividuals],
  );

  /**
   * Handles what happens when add member is clicked
   */
  const beginMemberSelection = useCallback(() => {
    openDialog({
      title: "Select Member",
      component: (
        <VendorTeamMemberSelection
          navigate={navigate}
          onNewSelection={handleNewMemberSelected}
          selectLeader={false}
          selectMember={true}
          selectedTeamMemberUserIds={getSelectedTeamMemberUserIds()}
          closeDialog={popDialog}
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }, [
    getSelectedTeamMemberUserIds,
    handleNewMemberSelected,
    navigate,
    openDialog,
    popDialog,
  ]);

  /**
   * Handles what happens when a member is removed from the vendor-team
   *
   */
  const handleLeaderRemoved = useCallback(async () => {
    const response = await confirm({
      title: "Remove Leader?",
      message: `Do you want to remove the leader from the team?`,
      okButtonText: "Remove",
    });

    if (response === ConfirmResponse.Cancel) return;

    onLeaderUpdated?.(null);
  }, [confirm, onLeaderUpdated]);

  /**
   * Handles what happens when a member is removed from the vendor-team
   *
   * @param userId The userId of the member to remove
   */
  const handleMemberRemoved = useCallback(
    async (userId?: Guid) => {
      if (!userId) return;

      const response = await confirm({
        title: "Remove Member?",
        message: `Do you want to remove the member from the team?`,
        okButtonText: "Remove",
      });

      if (response === ConfirmResponse.Cancel) return;

      onMemberRemoved?.(userId);
    },
    [confirm, onMemberRemoved],
  );

  const handleRemoveAllMembers = useCallback(async () => {
    const response = await confirm({
      title: "Remove All Members?",
      message: "Do you want to remove all members from the vendor-team?",
      okButtonText: "Remove All",
    });

    if (response === ConfirmResponse.Cancel) return;

    onMemberRemoved?.();
  }, [confirm, onMemberRemoved]);

  /**
   * Opens the user profile dialog for the given individual
   *
   * @param individualId The id of the individual to open the dialog for
   */
  const openUserProfileDialog = useCallback(
    (individualId: Guid) => {
      openDialog({
        component: <ViewIndividualProfile individualId={individualId} />,
        titleStyle: {
          paddingBottom: 0,
        },
        contentSxProps: {
          display: "flex",
          overflowX: "hidden",
        },
        MuiProps: {
          maxWidth: "lg",
          fullWidth: true,
        },
      });
    },
    [openDialog],
  );

  const renderLeader = useCallback(
    (leader: Individual, allowDelete?: boolean) => {
      return (
        <ListItem
          key={leader.userId.value ?? Guid.generate().value}
          secondaryAction={
            <>
              {!disableEditing && allowDelete && (
                <Tooltip title="Remove Leader">
                  <span>
                    <IconButton onClick={handleLeaderRemoved} size="medium">
                      <RemoveIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!hideCommentButton && (
                <CommentsButton
                  field={ProposalField.TeamLeader}
                  isActive={activeReviewField?.isEqualTo(
                    ProposalField.TeamLeader,
                  )}
                  disabled={disableComments}
                  toolTip={commentsToolTip}
                  commentThreads={commentThreads}
                  onCommentsClicked={
                    onCommentsClicked
                      ? () => {
                          onCommentsClicked(
                            ProposalField.TeamLeader,
                            leader.fullName,
                          );
                        }
                      : undefined
                  }
                />
              )}
            </>
          }
          disableGutters
        >
          <ListItemAvatar>
            <IndividualAvatar individual={leader} session={session} />
          </ListItemAvatar>
          <ListItemText
            primary={
              <Typography>
                <TeamMemberLink
                  onClick={() => openUserProfileDialog(leader.id)}
                >
                  {leader.fullName}
                </TeamMemberLink>
              </Typography>
            }
          ></ListItemText>
        </ListItem>
      );
    },
    [
      disableEditing,
      handleLeaderRemoved,
      hideCommentButton,
      activeReviewField,
      disableComments,
      commentsToolTip,
      commentThreads,
      onCommentsClicked,
      session,
      openUserProfileDialog,
    ],
  );

  /**
   * Renders a single vendor-team member's info as a list item
   *
   * @param member The member's info to render
   * @param allowDelete Whether or not to allow the member to be deleted
   */
  const renderMember = useCallback(
    (member: Individual, allowDelete?: boolean) => {
      return (
        <ListItem
          key={member.userId.value ?? Guid.generate().value}
          secondaryAction={
            <>
              {allowDelete && (
                <Tooltip title="Remove Member">
                  <span>
                    <IconButton
                      onClick={() => handleMemberRemoved(member.userId)}
                      size="medium"
                    >
                      <RemoveIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!hideCommentButton && (
                <CommentsButton
                  field={ProposalField.TeamMember(member.id)}
                  isActive={activeReviewField?.isEqualTo(
                    ProposalField.TeamMember(member.userId),
                  )}
                  toolTip={commentsToolTip}
                  disabled={disableComments}
                  commentThreads={commentThreads}
                  onCommentsClicked={
                    onCommentsClicked
                      ? () => {
                          onCommentsClicked(
                            ProposalField.TeamMember(member.userId),
                            member.fullName,
                          );
                        }
                      : undefined
                  }
                />
              )}
            </>
          }
          disableGutters
        >
          <ListItemAvatar>
            <IndividualAvatar individual={member} session={session} />
          </ListItemAvatar>
          <ListItemText
            primary={
              <Typography>
                <TeamMemberLink
                  onClick={() => openUserProfileDialog(member.id)}
                >
                  {member.fullName}
                </TeamMemberLink>
              </Typography>
            }
          ></ListItemText>
        </ListItem>
      );
    },
    [
      handleMemberRemoved,
      hideCommentButton,
      activeReviewField,
      disableComments,
      commentsToolTip,
      commentThreads,
      onCommentsClicked,
      session,
      openUserProfileDialog,
    ],
  );

  const handleMarketplaceTeamSelected = useCallback(
    (team: MarketplaceTeam) => {
      if (!team.leader.profile)
        throw new Error("Team leader profile is undefined");

      onMarketplaceTeamSelected?.(team);
      popDialog();
    },
    [onMarketplaceTeamSelected, popDialog],
  );

  const beginMarketplaceTeamSelection = useCallback(() => {
    openDialog({
      title: "Select Marketplace Team",
      component: (
        <VendorTeamSelection
          onNewSelection={handleMarketplaceTeamSelected}
          closeDialog={popDialog}
        />
      ),
      contentSxProps: {
        display: "flex",
        minHeight: "60vh",
      },
      MuiProps: {
        fullWidth: true,
        maxWidth: "lg",
      },
    });
  }, [handleMarketplaceTeamSelected, openDialog, popDialog]);

  return (
    <MainContainer className={className}>
      {userPermissions?.includesAccessToMarketplaceSearch &&
        !disableEditing &&
        session.accountType === AccountType.Client && (
          <FromMarketplaceButton
            variant="outlined"
            size="small"
            color="primary"
            onClick={beginMarketplaceTeamSelection}
          >
            From Marketplace Team
          </FromMarketplaceButton>
        )}
      <TeamContainer>
        <TeamList dense>
          <TeamListSubheader>
            <span>Team Leader</span>
          </TeamListSubheader>
          {!leaderIndividual && !session.context?.viewingAsVendor && (
            <>
              {!disableEditing && (!individualQuery.isLoading || !leader) && (
                <ListItemButton
                  dense
                  disableGutters
                  disabled={!onLeaderUpdated}
                  onClick={beginLeaderSelection}
                >
                  <ListItemAvatar>
                    <AddIcon color="primary" />
                  </ListItemAvatar>
                  <ListItemText>
                    <Typography color="primary">Select Leader</Typography>
                  </ListItemText>
                </ListItemButton>
              )}
              {!(individualQuery.isLoading && leader) && (
                <ListItem disableGutters>
                  <NoRowsPlaceholder color="textDisabled">
                    No Leader
                  </NoRowsPlaceholder>
                </ListItem>
              )}
            </>
          )}
          {!session.context?.viewingAsVendor &&
            leaderIndividual &&
            renderLeader(leaderIndividual, true)}
          {individualQuery.isLoading &&
            !session.context?.viewingAsVendor &&
            !leaderIndividual &&
            leader && <TeamLoader text="Loading..." />}
          {session.context?.viewingAsVendor &&
            (!individualQuery.isLoading || !leader) && (
              <>
                <TeamLeaderInput
                  disabled={
                    teamLeaderSelectorAnchor !== undefined || disableEditing
                  }
                  label={
                    leaderIndividual ? "Team Leader" : "Select Team Leader"
                  }
                  placeholder="No Leader Selected"
                  helperText="required"
                  required
                  autoComplete="off"
                  error={!leaderIndividual}
                  onClick={handleSelectTeamLeaderClicked}
                  value={
                    leaderIndividual ? `${leaderIndividual?.fullName}` : ""
                  }
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TeamLeaderSelector
                  popoverAnchor={teamLeaderSelectorAnchor}
                  teamLeaderUserId={leaderIndividual?.userId}
                  onTeamLeaderUpdated={handleCoworkerSelectedAsLeader}
                  onPopoverClose={() => {
                    setTeamLeaderSelectorAnchor(undefined);
                  }}
                />
              </>
            )}
        </TeamList>
        <TeamList dense>
          <TeamListSubheader>
            <span>Team Members</span>
            <div>
              {memberIndividuals &&
                memberIndividuals.length > 0 &&
                !disableEditing && (
                  <Tooltip title="Remove All Members">
                    <span>
                      <IconButton onClick={handleRemoveAllMembers}>
                        <RemoveIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              {!hideCommentButton && (
                <CommentsButton
                  field={ProposalField.Team}
                  isActive={activeReviewField?.isEqualTo(ProposalField.Team)}
                  toolTip={commentsToolTip}
                  disabled={disableComments}
                  commentThreads={commentThreads}
                  onCommentsClicked={
                    onCommentsClicked
                      ? () => {
                          onCommentsClicked(ProposalField.Team);
                        }
                      : undefined
                  }
                />
              )}
            </div>
          </TeamListSubheader>
          {!disableEditing &&
            !(individualQuery.isLoading && members.length) && (
              <ListItemButton
                dense
                disableGutters
                onClick={beginMemberSelection}
              >
                <ListItemAvatar>
                  <AddIcon color="primary" />
                </ListItemAvatar>
                <ListItemText>
                  <Typography color="primary">Add Member</Typography>
                </ListItemText>
              </ListItemButton>
            )}
          {((!individualQuery.isLoading && !memberIndividuals) ||
            members.length < 1) && (
            <ListItem disableGutters>
              <NoRowsPlaceholder color="textDisabled">
                No Members
              </NoRowsPlaceholder>
            </ListItem>
          )}
          {((!memberIndividuals.length &&
            !!members.length &&
            individualQuery.isLoading) ||
            undefined) && <TeamLoader text="Loading..." />}
          {memberIndividuals?.map((member) =>
            renderMember(member, !disableEditing),
          )}
        </TeamList>
      </TeamContainer>
    </MainContainer>
  );
}
